import {useEffect} from "react";
import {Map} from "ol";
import ZoomSlider from "ol/control/ZoomSlider";
import {unByKey} from "ol/Observable";
import {ZOOM} from "shared/constants/consts.common";

const setupZoomSlider = (map: Map) => {
  const TOOLTIP_HIDE_DELAY = 800;
  const zoomSlider = new ZoomSlider();
  map.addControl(zoomSlider);

  const view = map.getView();
  const zoomSliderElement = map.getTargetElement()?.querySelector(".ol-zoomslider") as HTMLElement | null;

  if (!zoomSliderElement) {
    return () => {
      map.removeControl(zoomSlider);
    };
  }

  const tooltipElement = document.createElement("div");
  tooltipElement.className = "ol-zoomslider-tooltip";
  zoomSliderElement.appendChild(tooltipElement);
  let tooltipTimer: ReturnType<typeof setTimeout> | undefined;

  const hideTooltip = () => {
    tooltipElement.dataset.visible = "false";
  };

  const showTooltipTemporarily = () => {
    tooltipElement.dataset.visible = "true";

    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
    }

    tooltipTimer = setTimeout(() => {
      hideTooltip();
      tooltipTimer = undefined;
    }, TOOLTIP_HIDE_DELAY);
  };

  const updateTooltip = () => {
    const currentZoom = view.getZoom() ?? ZOOM;
    const thumbElement = zoomSliderElement.querySelector(".ol-zoomslider-thumb") as HTMLElement | null;

    tooltipElement.textContent = String(Math.floor(currentZoom));

    if (thumbElement) {
      const thumbCenterY = thumbElement.offsetTop + (thumbElement.offsetHeight / 2);
      tooltipElement.style.top = `${thumbCenterY}px`;
      tooltipElement.style.bottom = "auto";
    }
  };

  updateTooltip();

  const resolutionChangeKey = view.on("change:resolution", updateTooltip);
  
  const handleSliderInteraction = () => {
    updateTooltip();
    showTooltipTemporarily();
  };

  zoomSliderElement.addEventListener("pointermove", handleSliderInteraction);
  zoomSliderElement.addEventListener("click", handleSliderInteraction);
  hideTooltip();

  return () => {
    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
    }

    zoomSliderElement.removeEventListener("pointerdown", handleSliderInteraction);
    zoomSliderElement.removeEventListener("click", handleSliderInteraction);
    unByKey(resolutionChangeKey);
    tooltipElement.remove();
    map.removeControl(zoomSlider);
  };
};

export const useZoomSlider = (map?: Map) => {
  useEffect(() => {
    if (!map) {
      return;
    }

    return setupZoomSlider(map);
  }, [map]);
};
